import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material"
import { clientApi, generalApi, userApi } from "../../api"
import PersonSelect from "../../components/PersonSelect"
import { COMMON_INDUSTRIES } from "./ClientDetailDialog"

const EMPTY = {
  id: "",
  name: "",
  label: "",
  description: "",
  carrier_id: "",
  industry: "",
  owner_user_id: "",
  timezone: "UTC",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
}

const EMPTY_ADMIN = { username: "", email: "", name: "" }

/**
 * Add a client.
 *
 * This is the one form in the app whose submit runs DDL: it registers the
 * organization and builds its MySQL schema. Two consequences shape it:
 *
 *   1. The ID IS THE SCHEMA NAME and cannot be changed afterwards — moving a
 *      client between ids means moving a database. It is suggested rather than
 *      typed by default, and the form says what it is.
 *   2. THE CARRIER CANNOT BE CHANGED afterwards either, because it decides which
 *      tables the schema gets. Changing the row later would leave a Boost tenant
 *      pointing at Metro while holding Boost tables, and the failure surfaces at
 *      the first import as a missing table nobody connects back to this screen.
 *
 * The first administrator is optional but offered here, because a client with a
 * database and nobody who can sign in is a half-finished onboarding that someone
 * has to remember to come back to.
 */
export default function AddClientDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY)
  const [admin, setAdmin] = useState(EMPTY_ADMIN)
  const [withAdmin, setWithAdmin] = useState(true)
  const [carriers, setCarriers] = useState([])
  const [staff, setStaff] = useState([])
  const [industries, setIndustries] = useState([])
  const [error, setError] = useState(null)
  const [warning, setWarning] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setForm(EMPTY)
    setAdmin(EMPTY_ADMIN)
    setError(null)
    setWarning(null)

    generalApi
      .carriers()
      .then((data) => setCarriers(data.carriers || []))
      .catch(() => setCarriers([]))
    userApi
      .list({ client_id: "0", limit: 100 })
      .then((data) => setStaff((data.users || []).filter((person) => person.active)))
      .catch(() => setStaff([]))
    clientApi
      .industries()
      .then((data) => setIndustries(data.industries || []))
      .catch(() => setIndustries([]))

    // Suggested, not forced. A numeric id is the convention; "TW-1042" is equally
    // valid, which is why the field stays editable.
    clientApi
      .nextId()
      .then((data) => setForm((f) => ({ ...f, id: data.id })))
      .catch(() => {})
  }, [open])

  const industryOptions = useMemo(
    () => [...new Set([...industries, ...COMMON_INDUSTRIES])].sort((a, b) => a.localeCompare(b)),
    [industries],
  )

  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }))
  const setAdminField = (key) => (event) => setAdmin((a) => ({ ...a, [key]: event.target.value }))

  const submit = async (event) => {
    event.preventDefault()
    setError(null)
    setWarning(null)
    setBusy(true)

    try {
      const payload = {
        id: form.id.trim(),
        name: form.name.trim(),
        label: form.label.trim() || form.name.trim(),
        description: form.description.trim() || null,
        carrier_id: form.carrier_id === "" ? null : Number(form.carrier_id),
        industry: form.industry.trim() || null,
        owner_user_id: form.owner_user_id ? Number(form.owner_user_id) : null,
        timezone: form.timezone,
        contact: { name: form.contact_name, email: form.contact_email, phone: form.contact_phone },
        ...(withAdmin && admin.email ? { admin } : {}),
      }

      const result = await clientApi.create(payload)

      /*
       * A 201 does not mean everything worked.
       *
       * The server registers the client, commits, then builds the schema — so a
       * schema failure leaves a registered client with `schema_provisioned = 0`,
       * which is recoverable from the client list. Reporting it here is the
       * difference between "fix this now" and discovering it a week later.
       */
      // An invite that was never sent belongs here too: the client and the
      // account both exist, and the one person who could sign in has not been
      // told. Silence there looks identical to success.
      const inviteFailed = result.admin_user && !result.admin_email_sent
      if (!result.schema_provisioned || result.admin_error || inviteFailed) {
        setWarning(
          [
            result.schema_provisioned ? null : `The database could not be built: ${result.provision_error}. Use the build icon on the client list to retry.`,
            result.admin_error ? `The administrator account was not created: ${result.admin_error}` : null,
            inviteFailed
              ? result.email_configured
                ? `${result.admin_user.username} was created, but their invite failed to send: ${result.admin_email_error}`
                : `${result.admin_user.username} was created, but no invite could be sent — email is not configured on this deployment.`
              : null,
          ]
            .filter(Boolean)
            .join(" "),
        )
        return
      }

      onCreated(result)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>Add client</DialogTitle>
        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          {warning ? (
            <Alert severity="warning" sx={{ mb: 2 }} action={<Button size="small" onClick={() => onCreated()}>Close</Button>}>
              The client was added, but: {warning}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField label="Display name" value={form.label} onChange={set("label")} fullWidth autoFocus
                placeholder="Acme Wireless" helperText="What people see in the app" />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField label="Short name" value={form.name} onChange={set("name")} fullWidth required
                placeholder="acme" helperText="Unique, lowercase" />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Client id"
                value={form.id}
                onChange={set("id")}
                fullWidth
                required
                helperText="Also the database name. Cannot be changed later."
                slotProps={{ input: { sx: { fontFamily: "monospace" } } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo
                options={industryOptions}
                value={form.industry}
                onInputChange={(_event, value) => setForm((f) => ({ ...f, industry: value }))}
                renderInput={(params) => <TextField {...params} label="Industry" helperText="What they do. Pick one, or type your own" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PersonSelect
                people={staff}
                value={form.owner_user_id}
                onChange={(value) => setForm((f) => ({ ...f, owner_user_id: value || "" }))}
                label="Owner"
                emptyLabel="Nobody yet"
                helperText="Who on our side looks after them"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {/* Not the industry. For a wireless dealer this adds the carrier's
                  reconciliation tables to their database, which is why it is
                  fixed once set; every other client leaves it at None. */}
              <TextField
                select
                label="Wireless carrier"
                value={form.carrier_id}
                onChange={set("carrier_id")}
                fullWidth
                helperText="Only for wireless dealers. Cannot be changed later."
                slotProps={{ select: { displayEmpty: true } }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {carriers.map((carrier) => (
                  <MenuItem key={carrier.id} value={carrier.id}>
                    {carrier.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={12}>
              <TextField label="Description" value={form.description} onChange={set("description")} fullWidth multiline rows={2} />
            </Grid>

            <Grid size={12}>
              <Divider sx={{ my: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  PRIMARY CONTACT
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Name" value={form.contact_name} onChange={set("contact_name")} fullWidth size="small" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Email" type="email" value={form.contact_email} onChange={set("contact_email")} fullWidth size="small" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Phone" value={form.contact_phone} onChange={set("contact_phone")} fullWidth size="small" />
            </Grid>

            <Grid size={12}>
              <Divider sx={{ my: 0.5 }} />
              <FormControlLabel
                control={<Checkbox checked={withAdmin} onChange={(e) => setWithAdmin(e.target.checked)} />}
                label="Create their first administrator now"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
                Without one, nobody at this client can sign in yet.
              </Typography>
            </Grid>

            {withAdmin ? (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Administrator username"
                    value={admin.username}
                    onChange={setAdminField("username")}
                    fullWidth
                    size="small"
                    required={withAdmin}
                    helperText="What they sign in with. Must be unique."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField label="Administrator email" type="email" value={admin.email} onChange={setAdminField("email")} fullWidth size="small" required={withAdmin} />
                </Grid>
                <Grid size={12}>
                  <TextField label="Administrator name" value={admin.name} onChange={setAdminField("name")} fullWidth size="small" />
                </Grid>
                <Grid size={12}>
                  {/* No password. Supplying one suppressed the invite, so the
                      email with the set-password link never went out — and it
                      left the administrator holding a credential they then had
                      to pass along out of band. */}
                  <Alert severity="info" variant="outlined">
                    They will get an email with a link to choose their own password.
                  </Alert>
                </Grid>
              </>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            {busy ? "Creating…" : "Add client"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
